import { gql, useMutation, useQuery } from '@apollo/client'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { type DragEvent, useMemo, useRef, useState } from 'react'
import { Title, useLocale, useTranslate } from 'react-admin'
import { fileStorage } from '~/helpers'

/**
 * "Gebrauchtyachtkatalog": an issue is uploaded here as it comes from the
 * layout program (100 MB and more); the feed service makes the web version
 * and the cover (Ghostscript, nautix/lib/catalog.mjs), and the website picks
 * the current issue up from feed.<domain>/katalog.json by itself.
 */
const CATALOGS = gql`
  query Catalogs {
    catalogs(order_by: { issue: desc }) {
      id
      issue
      published_at
      pages
      original_size
      pdf_size
      original_file_id
      pdf_file_id
      cover_file_id
    }
  }
`

const PUBLISH = gql`
  mutation CatalogPublish($original_file_id: uuid!, $issue: Int!) {
    catalog_publish(original_file_id: $original_file_id, issue: $issue) {
      issue
      pages
      original_size
      pdf_size
    }
  }
`

interface Catalog {
  id: number
  issue: number
  published_at: string | null
  pages: number | null
  original_size: number | null
  pdf_size: number | null
  original_file_id: string
  pdf_file_id: string | null
  cover_file_id: string | null
}

/** Files of the public-by-id `catalogs` bucket, on this origin. */
const fileHref = (id: string | null) => (id ? `/storage/v1/files/${id}` : undefined)

/** "GYZ Ausgabe 34.pdf" → 34: the last number in the name. */
const issueFromName = (name: string) => {
  const numbers = name.replace(/\.pdf$/i, '').match(/\d+/g)
  return numbers ? Number(numbers[numbers.length - 1]) : null
}

export const CatalogPage = () => {
  const translate = useTranslate()
  const locale = useLocale()
  const { data, refetch } = useQuery<{ catalogs: Catalog[] }>(CATALOGS, {
    fetchPolicy: 'cache-and-network',
  })
  const [publish] = useMutation(PUBLISH)
  const [file, setFile] = useState<File | null>(null)
  const [issue, setIssue] = useState('')
  const [phase, setPhase] = useState<'uploading' | 'processing' | null>(null)
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(
    null
  )
  const input = useRef<HTMLInputElement>(null)
  const size = useMemo(() => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }), [locale])
  const date = useMemo(() => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }), [locale])
  const mb = (bytes: number | null) => (bytes == null ? '–' : `${size.format(bytes / 1e6)} MB`)

  const catalogs = data?.catalogs ?? []
  const current = catalogs.find((c) => c.published_at) ?? null
  const issueNumber = Number(issue)
  const replaces = catalogs.some((c) => c.issue === issueNumber)

  const choose = (chosen: File | undefined) => {
    if (!chosen) return
    setMessage(null)
    if (!/pdf$/i.test(chosen.type) && !/\.pdf$/i.test(chosen.name)) {
      setMessage({ severity: 'error', text: translate('catalog.not_pdf') })
      return
    }
    setFile(chosen)
    setIssue(String(issueFromName(chosen.name) ?? (current ? current.issue + 1 : '')))
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    choose(e.dataTransfer.files?.[0])
  }

  const submit = async () => {
    if (!file || !Number.isInteger(issueNumber) || issueNumber < 1) return
    setMessage(null)
    let uploaded: string | null = null
    try {
      setPhase('uploading')
      uploaded = (await fileStorage.put(file, 'catalogs')).key
      setPhase('processing')
      const { data: result } = await publish({
        variables: { original_file_id: uploaded, issue: issueNumber },
      })
      const done = result.catalog_publish
      setMessage({
        severity: 'success',
        text: translate('catalog.published', {
          issue: done.issue,
          original: mb(done.original_size),
          web: mb(done.pdf_size),
          pages: done.pages ?? '?',
        }),
      })
      setFile(null)
      setIssue('')
      refetch()
    } catch (e: any) {
      // The original was not taken up: it does not stay in the bucket.
      if (uploaded) await fileStorage.remove(uploaded).catch(() => {})
      setMessage({ severity: 'error', text: e.message })
    } finally {
      setPhase(null)
    }
  }

  return (
    <Box sx={{ p: 2, minWidth: 0 }}>
      <Title title={translate('catalog.title')} />
      <Typography variant='body1' color='text.secondary' sx={{ mb: 2, maxWidth: 900 }}>
        {translate('catalog.intro')}
      </Typography>

      {message && (
        <Alert
          severity={message.severity}
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
          data-testid='catalog-message'
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant='outlined' sx={{ height: '100%' }} data-testid='catalog-current'>
            <CardContent>
              <Typography variant='h6' sx={{ mb: 1 }}>
                {translate('catalog.current')}
              </Typography>
              {current ? (
                <Stack direction='row' spacing={2}>
                  {current.cover_file_id && (
                    <Box
                      component='img'
                      src={fileHref(current.cover_file_id)}
                      alt={translate('catalog.cover', { issue: current.issue })}
                      sx={{ width: 140, height: 'auto', boxShadow: 2, borderRadius: 0.5 }}
                    />
                  )}
                  <Box>
                    <Typography variant='h5'>
                      {translate('catalog.issue', { issue: current.issue })}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                      {translate('catalog.since', {
                        date: date.format(new Date(current.published_at as string)),
                      })}
                      {current.pages
                        ? ` · ${translate('catalog.pages', { pages: current.pages })}`
                        : ''}
                    </Typography>
                    <Typography variant='body2'>
                      <MuiLink href={fileHref(current.pdf_file_id)} target='_blank' rel='noopener'>
                        {translate('catalog.web_pdf')}
                      </MuiLink>{' '}
                      ({mb(current.pdf_size)})
                    </Typography>
                    <Typography variant='body2'>
                      <MuiLink
                        href={fileHref(current.original_file_id)}
                        target='_blank'
                        rel='noopener'
                      >
                        {translate('catalog.original')}
                      </MuiLink>{' '}
                      ({mb(current.original_size)})
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Typography color='text.secondary'>{translate('catalog.none')}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Card variant='outlined' sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant='h6' sx={{ mb: 1 }}>
                {translate('catalog.new')}
              </Typography>
              <Box
                role='button'
                tabIndex={0}
                onClick={() => input.current?.click()}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                data-testid='catalog-drop'
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 3,
                  mb: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <UploadFileIcon color='action' />
                <Typography>
                  {file ? `${file.name} (${mb(file.size)})` : translate('catalog.drop')}
                </Typography>
                <input
                  ref={input}
                  type='file'
                  accept='application/pdf,.pdf'
                  hidden
                  data-testid='catalog-file'
                  onChange={(e) => choose(e.target.files?.[0])}
                />
              </Box>
              <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
                <TextField
                  size='small'
                  label={translate('catalog.issue_number')}
                  value={issue}
                  onChange={(e) => setIssue(e.target.value.replace(/\D/g, ''))}
                  helperText={replaces ? translate('catalog.replaces', { issue }) : ' '}
                  slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                  sx={{ width: 160 }}
                />
                <Button
                  variant='contained'
                  disabled={!file || !issueNumber || !!phase}
                  onClick={submit}
                  data-testid='catalog-publish'
                >
                  {translate('catalog.publish')}
                </Button>
              </Stack>
              {phase && (
                <Alert
                  severity='info'
                  icon={<CircularProgress size={18} />}
                  sx={{ mt: 1 }}
                  data-testid='catalog-progress'
                >
                  {phase === 'uploading'
                    ? translate('catalog.uploading', { size: mb(file?.size ?? null) })
                    : translate('catalog.processing')}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {catalogs.length > 0 && (
        <Card variant='outlined' sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant='h6' sx={{ mb: 1 }}>
              {translate('catalog.all')}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size='small' data-testid='catalog-list'>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('catalog.issue_number')}</TableCell>
                    <TableCell>{translate('catalog.published_on')}</TableCell>
                    <TableCell align='right'>{translate('catalog.pages_column')}</TableCell>
                    <TableCell align='right'>{translate('catalog.web_pdf')}</TableCell>
                    <TableCell align='right'>{translate('catalog.original')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catalogs.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.issue}</TableCell>
                      <TableCell>
                        {c.published_at ? date.format(new Date(c.published_at)) : '–'}
                      </TableCell>
                      <TableCell align='right'>{c.pages ?? '–'}</TableCell>
                      <TableCell align='right'>
                        <MuiLink href={fileHref(c.pdf_file_id)} target='_blank' rel='noopener'>
                          {mb(c.pdf_size)}
                        </MuiLink>
                      </TableCell>
                      <TableCell align='right'>
                        <MuiLink href={fileHref(c.original_file_id)} target='_blank' rel='noopener'>
                          {mb(c.original_size)}
                        </MuiLink>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default CatalogPage
